// src/App.js
import React, { useState, useMemo, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { ThemeProvider, createTheme, alpha } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import './supabaseClient';
import './api/axiosConfig';

import { AuthProvider } from './context/AuthContext';
import { AgentProvider } from './context/AgentContext';
import { BillingProvider } from './context/BillingContext';
import ProtectedRoute from './components/common/ProtectedRoute';
import AdminLayout from './components/layout/AdminLayout';
import AgentWorkspaceLayout from './components/layout/AgentWorkspaceLayout';

import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import AgentsList from './pages/agents/AgentsList';
import AgentEditor from './pages/agents/AgentEditor';
import AgentOverviewTab from './pages/agents/AgentOverviewTab';
import AgentInteractTab from './pages/agents/AgentInteractTab';
import AgentSettingsTab from './pages/agents/AgentSettingsTab';
import AgentPromptTab from './pages/agents/AgentPromptTab';
import AgentToolsTab from './pages/agents/AgentToolsTab';
import AgentWidgetsTab from './pages/agents/AgentWidgetsTab';
import PromptList from './pages/prompts/PromptList';
import PromptEditor from './pages/prompts/PromptEditor';
import WidgetTemplateListPage from './pages/widgets/WidgetTemplateListPage';
import WidgetTemplateEditorPage from './pages/widgets/WidgetTemplateEditorPage';
import WidgetVersionListPage from './pages/widgets/WidgetVersionListPage';
import WidgetVersionEditorPage from './pages/widgets/WidgetVersionEditorPage';
import ToolList from './pages/tools/ToolList';
import ToolEditor from './pages/tools/ToolEditor';
import AgentToolsDocumentation from './pages/tools/AgentToolsDocumentation';
import ConversationList from './pages/conversations/ConversationList';
import ConversationDetail from './pages/conversations/ConversationDetail';
import ApiKeys from './pages/apikeys/ApiKeys';
import Terms from './pages/legal/Terms';
import Privacy from './pages/legal/Privacy';
import TestPage from './pages/TestPage';
import PlatformSettingsPage from './pages/PlatformSettingsPage';
import HelpPage from './pages/HelpPage';
import BillingPage from './pages/BillingPage';


const AgentSpecificConversationList = () => {
  const { agentId } = useParams();
  return <ConversationList agentIdForFilter={agentId} />; // Ensure prop name matches ConversationList expectation
};

const createAppTheme = (mode) => createTheme({
  palette: {
    mode,
    ...(mode === 'light'
      ? { // Light Mode
          primary: { main: '#007AFF', light: '#58AFFF', dark: '#0056B3', contrastText: '#ffffff' },
          secondary: { main: '#FF9500', light: '#FFB34D', dark: '#CC7A00', contrastText: '#ffffff' },
          background: { default: '#F8F9FA', paper: '#FFFFFF' },
          text: { primary: '#1D1D1F', secondary: '#6E6E73' },
          divider: alpha('#000000', 0.12),
          info: { main: '#007AFF', light: '#58AFFF', dark: '#0056B3', contrastText: '#ffffff'},
          success: { main: '#34C759', light: '#7AF496', dark: '#248A3D', contrastText: '#ffffff' },
          warning: { main: '#FFCC00', light: '#FFDA4D', dark: '#CCA300', contrastText: '#1D1D1F' },
          error: { main: '#FF3B30', light: '#FF7066', dark: '#B32A22', contrastText: '#ffffff' }
        }
      : { // Dark Mode
          primary: { main: '#0A84FF', light: '#59AFFF', dark: '#0060D1', contrastText: '#ffffff' },
          secondary: { main: '#FF9F0A', light: '#FFB956', dark: '#B36B00', contrastText: '#ffffff' },
          background: { default: '#1C1C1E', paper: '#2C2C2E' },
          text: { primary: '#F2F2F7', secondary: '#AEAEB2' },
          divider: alpha('#FFFFFF', 0.15),
          info: { main: '#0A84FF', light: '#59AFFF', dark: '#0060D1', contrastText: '#ffffff'},
          success: { main: '#30D158', light: '#6AF48B', dark: '#218A3B', contrastText: '#ffffff' },
          warning: { main: '#FFD60A', light: '#FFE051', dark: '#B39500', contrastText: '#1D1D1F' }, // CORRECTED: '#FFEO51' to '#FFE051'
          error: { main: '#FF453A', light: '#FF7B73', dark: '#D13128', contrastText: '#ffffff' }
        }),
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontWeight: 700, fontSize: '2.8rem', lineHeight: 1.2, letterSpacing: '-0.01em' },
    h2: { fontWeight: 700, fontSize: '2.2rem', lineHeight: 1.2, letterSpacing: '-0.005em' },
    h3: { fontWeight: 600, fontSize: '1.8rem', lineHeight: 1.25, letterSpacing: '0em' },
    h4: { fontWeight: 600, fontSize: '1.5rem', lineHeight: 1.3, letterSpacing: '0.005em' },
    h5: { fontWeight: 600, fontSize: '1.25rem', lineHeight: 1.3, letterSpacing: '0em' },
    h6: { fontWeight: 600, fontSize: '1.1rem', lineHeight: 1.3, letterSpacing: '0.005em' },
    body1: { fontSize: '1rem', lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', lineHeight: 1.5 },
    button: { fontWeight: 600, textTransform: 'none', letterSpacing: '0.01em' },
    caption: { fontSize: '0.75rem', lineHeight: 1.4 },
  },
  shape: {
    borderRadius: 10,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          borderRadius: theme.shape.borderRadius,
          textTransform: 'none',
          fontWeight: 600,
          boxShadow: 'none',
          padding: '8px 18px',
          '&:hover': {
            boxShadow: 'none',
            ...(ownerState.variant === 'contained' && {
              backgroundColor: theme.palette.mode === 'light' ?
                                theme.palette[ownerState.color || 'primary']?.dark :
                                theme.palette[ownerState.color || 'primary']?.light,
            }),
          },
        }),
        sizeLarge: { padding: '10px 22px', fontSize: '1rem' },
        sizeMedium: { padding: '8px 18px', fontSize: '0.9375rem' },
        sizeSmall: { padding: '6px 12px', fontSize: '0.8125rem' },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation0: ({theme}) => ({
           border: `1px solid ${theme.palette.divider}`,
           boxShadow: 'none',
        }),
        elevation1: ({ theme }) => ({
          boxShadow: theme.palette.mode === 'dark' ?
            '0px 4px 12px rgba(0,0,0,0.15)' :
            '0px 2px 8px rgba(0,0,0,0.06)',
        }),
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          boxShadow: `0px 1px 0px ${theme.palette.divider}`,
        }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
          borderRight: theme.palette.mode === 'light' ? `1px solid ${theme.palette.divider}` : 'none',
        }),
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: ({ theme }) => ({ 
          '& .MuiOutlinedInput-root': {
            borderRadius: theme.shape.borderRadius - 2 > 0 ? theme.shape.borderRadius - 2 : 4,
          },
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({theme}) => ({
            borderRadius: theme.shape.borderRadius - 2 > 0 ? theme.shape.borderRadius -2 : 4,
        }),
        notchedOutline: ({ theme }) => ({
          borderColor: theme.palette.divider,
        }),
      },
    },
    MuiTabs: {
        styleOverrides: {
            indicator: ({theme}) => ({
                backgroundColor: theme.palette.primary.main,
                height: '3px',
                borderRadius: '3px 3px 0 0'
            }),
        }
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          textTransform: 'none',
          fontWeight: 500,
          minHeight: '48px',
          '&.Mui-selected': {
            fontWeight: 700,
            color: theme.palette.primary.main,
          },
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({theme}) => ({
          borderRadius: '6px',
          fontWeight: 500,
          padding: '4px 8px',
        }),
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: ({ theme }) => ({
          backgroundColor: theme.palette.mode === 'light' ? alpha(theme.palette.common.black, 0.85) : alpha(theme.palette.common.white, 0.85),
          color: theme.palette.mode === 'light' ? theme.palette.common.white : theme.palette.common.black,
          fontSize: '0.8rem',
          padding: '6px 10px',
          borderRadius: theme.shape.borderRadius / 2,
        }),
      },
    },
    MuiCard: { 
        styleOverrides: {
            root: ({theme}) => ({
                borderRadius: theme.shape.borderRadius,
            })
        }
    },
    MuiCardHeader: {
        styleOverrides: {
            root: {
                padding: '16px 20px', 
            },
            title: ({theme}) => ({
                fontSize: theme.typography.h6.fontSize, 
                fontWeight: theme.typography.h6.fontWeight,
            }),
            subheader: ({theme}) => ({
                fontSize: theme.typography.body2.fontSize,
            })
        }
    },
    MuiCardContent: {
        styleOverrides: {
            root: {
                padding: '16px 20px', 
                '&:last-child': {
                    paddingBottom: '16px', 
                },
            }
        }
    },
    MuiBreadcrumbs: {
        styleOverrides: {
            li: ({theme}) => ({
                '& .MuiLink-root': { 
                    color: theme.palette.text.secondary,
                    textDecoration: 'none',
                    '&:hover': {
                        color: theme.palette.primary.main,
                    }
                },
                '& .MuiTypography-root': { 
                    color: theme.palette.text.primary,
                    fontWeight: 500,
                }
            }),
            separator: ({theme}) => ({
                color: theme.palette.text.disabled,
            })
        }
    }
  },
});


function App() {
  const getInitialMode = () => {
    const saved = localStorage.getItem('themeMode');
    if (saved) return saved;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  };
  const [mode, setMode] = useState(getInitialMode);
  const theme = useMemo(() => createAppTheme(mode), [mode]);
  useEffect(() => { localStorage.setItem('themeMode', mode); }, [mode]);
  const toggleTheme = () => setMode((m) => (m === 'light' ? 'dark' : 'light'));

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AuthProvider>
          <AgentProvider>
          <BillingProvider> {/* BillingProvider is included */}
            <Routes>
              {/* Public */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />

              {/* Protected Routes */}
              <Route element={<ProtectedRoute />}>
                <Route element={<AdminLayout toggleTheme={toggleTheme} currentTheme={mode} />}>
                  {/* Global Views */}
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/agents" element={<AgentsList />} />
                  <Route path="/agents/new" element={<AgentEditor />} />
                  
                  <Route path="/agent-workspace/:agentId/*" element={<AgentWorkspaceLayout />}>
                    <Route index element={<Navigate to="overview" replace />} />
                    <Route path="overview" element={<AgentOverviewTab />} />
                    <Route path="interact" element={<AgentInteractTab />} />
                    <Route path="settings" element={<AgentSettingsTab />} />
                    <Route path="prompt" element={<AgentPromptTab />} />
                    <Route path="tools" element={<AgentToolsTab />} />
                    <Route path="widgets" element={<AgentWidgetsTab />} />
                    <Route path="conversations" element={<AgentSpecificConversationList />} />
                    <Route path="*" element={<Navigate to="overview" replace />} />
                  </Route>

                  <Route path="/prompts" element={<PromptList />} />
                  <Route path="/prompts/create" element={<PromptEditor />} />
                  <Route path="/prompts/edit/:id" element={<PromptEditor />} />

                  <Route path="/tools" element={<ToolList />} />
                  <Route path="/tools/new" element={<ToolEditor />} />
                  <Route path="/tools/:id/edit" element={<ToolEditor />} />
                  <Route path="/tools/documentation" element={<AgentToolsDocumentation />} />

                  <Route path="/widgets" element={<WidgetTemplateListPage />} />
                  <Route path="/widgets/new" element={<WidgetTemplateEditorPage />} />
                  <Route path="/widgets/:templateId/edit" element={<WidgetTemplateEditorPage />} />
                  <Route path="/widgets/:templateId/versions" element={<WidgetVersionListPage />} />
                  <Route path="/widgets/:templateId/versions/new" element={<WidgetVersionEditorPage />} />
                  <Route path="/widgets/versions/:versionId/edit" element={<WidgetVersionEditorPage />} />

                  <Route path="/conversations" element={<ConversationList />} />
                  <Route path="/conversations/:id" element={<ConversationDetail />} />

                  <Route path="/apikeys" element={<ApiKeys />} />
                  <Route path="/billing" element={<BillingPage />} /> {/* Billing route is included */}
                  <Route path="/platform-settings" element={<PlatformSettingsPage />} />
                  <Route path="/help" element={<HelpPage />} />
                  
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/widget-test" element={<TestPage />} />

                  <Route index element={<Navigate to="/dashboard" replace />} />
                </Route>
              </Route>

              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="*" element={<Navigate to="/login" />} />
            </Routes>
            </BillingProvider>
          </AgentProvider>
          <ToastContainer
            position="top-right"
            autoClose={3500}
            hideProgressBar={false}
            newestOnTop={true}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme={mode} // Dynamically set based on current mode
            toastStyle={{
              borderRadius: theme.shape.borderRadius, // Use theme's border radius
              boxShadow: theme.shadows[4], // Use a standard theme shadow
              fontSize: '0.9rem', // Consistent font size
              // backgroundColor: theme.palette.background.paper, // Optional: use paper background for toast
              // color: theme.palette.text.primary, // Optional: use primary text color
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;